import Medusa from "@medusajs/js-sdk"
import { t, ts } from "./i18n"

/**
 * HQ talks to Medusa on its OWN origin: hq.highclub.store/admin/* and /auth/* are passed through to
 * the backend by the Worker (worker/index.ts), and by Vite's proxy in development. That keeps
 * Medusa's SameSite=Lax session cookie first-party, which is what lets an iPhone stay signed in.
 * Every HQ screen reads and writes through Medusa's admin API (including HQ's own /admin/hq/*);
 * nothing is stored in this app.
 */
export const sdk = new Medusa({
  baseUrl: window.location.origin,
  auth: { type: "session" },
})

/** Medusa's own dashboard, for the heavy edits HQ hands off (e.g. "/orders/order_..."). */
const MEDUSA_ADMIN_ORIGIN = (import.meta.env.VITE_MEDUSA_ADMIN_URL as string | undefined) ?? "http://localhost:9000"
export const medusaAdminUrl = (path = "") => `${MEDUSA_ADMIN_ORIGIN}/app${path}`

export type HqHealth = {
  ok: boolean
  /** Taobao / Tmall sizes and stock (TMAPI). */
  taobao_reader?: { configured: boolean; with_1688: boolean }
  commit: string | null
  email_mode: "live" | "owner_only" | "off"
  athens_date: string
  snapshots: {
    total_rows: number
    rows_today: number
    last_read_at: string | null
  }
  custom_sheets?: {
    configured: boolean
    last_change_at: string | null
    checked_at: string | null
    orders: number | null
    larry_error: string | null
  }
}

import type { CostSettings } from "./cost-settings"

export type CheckIssue = { kind: string; severity: "fix" | "look"; ref: string; link: string | null; message: string; amount: number | null }
export type ChecksReport = {
  days: number
  checked_at: string
  summary: { fix: number; look: number }
  card: { card_orders: number; captured: number; refunded: number; fees_read: number; fee_total: number; fees_not_read: number; issues: CheckIssue[] }
  cash: { owed: number; issues: CheckIssue[] }
  facts: { orders: number; in_facts: number; issues: CheckIssue[] }
  links: { paid_links: number; total: number; fees: number; issues: CheckIssue[] }
  partner: { months: Array<{ month: string; orders: number; compared: number; revenue: number; hq: number; partner: number; difference: number }>; biggest: CheckIssue[] }
  shopify: { imported: { orders: number; net_revenue: number; first: string | null; last: string | null; at: string | null } | null; held: { orders: number; net_revenue: number }; matches: boolean; issues: CheckIssue[] }
  /** The storefront tracker in shadow mode: n8n and HQ asked the same number. Absent on an older backend. */
  tracking?: {
    numbers: number
    lookups: number
    agree: number
    by_difference: Record<string, number>
    since: string | null
    differences: Array<CheckIssue & { n8n: number | string | null; hq: number | string | null; last_seen_at: string }>
  }
}

export type PushTopic = "orders" | "alerts"
export type PushDevice = { id: string; endpoint: string; label: string | null; topics: PushTopic[]; created_at: string; last_sent_at: string | null; failures: number }
export type PushState = { configured: boolean; public_key: string | null; topics: PushTopic[]; devices: PushDevice[] }

export type Queue = "to_ship" | "to_arrange" | "cash_to_collect" | "done" | "canceled"
export type DeliveryMethod = "box_now" | "home" | "meetup" | "unknown"

export type OrderListItem = {
  id: string
  ref: string
  display_id: number | null
  created_at: string | null
  status: string | null
  customer: string
  instagram: string | null
  delivery: DeliveryMethod
  payment: { kind: "card" | "cash" | "unknown"; collected: boolean }
  total: number
  currency: string
  units: number
  first_item: string | null
  thumbnail: string | null
  queues: Queue[]
  shipped: boolean
  delivered: boolean
  tracking: string | null
  /** An exchange recorded in HQ whose new item has not gone out yet (the order is then in To arrange). */
  exchange_to_send?: boolean
}

export type ReturnReason = "size" | "changed_mind" | "faulty" | "wrong_item" | "other"
export type ReturnWay = "box_now" | "meetup" | "courier" | "other"
export type OrderReturn = {
  id: string
  created_at: string | null
  outcome: "refund" | "exchange"
  reason: string
  way: string | null
  note: string | null
  items: Array<{ line_item_id: string; variant_id: string | null; title: string; option: string; quantity: number; unit_price: number; restock: boolean; extra: boolean }>
  exchange_items: Array<{ variant_id: string; title: string; option: string; quantity: number; unit_price: number }>
  returned_value: number
  exchange_value: number | null
  /** Exchange: new − returned. Above zero the customer owes it; below zero the shop does. */
  difference: number | null
  refund_amount: number | null
  exchange_status: "none" | "to_send" | "sent"
  exchange_sent_at: string | null
  exchange_way: string | null
  exchange_tracking: string | null
  emailed: boolean
}
export type ReturnBody = {
  request_id: string
  items: Array<{ line_item_id: string; quantity: number; restock: boolean | null }>
  reason: ReturnReason
  way: ReturnWay | null
  note: string | null
  outcome: "refund" | "exchange"
  refund_amount: number | null
  exchange_items: Array<{ variant_id: string; quantity: number }> | null
  notify: boolean
}

/** The four lines the BOX NOW portal asks for (lib/hq/boxnow-contact.ts in the backend). */
export type BoxNowLines = { name: string; email: string; phone: string; address: string; complete: boolean }
export type OrderDetail = Omit<OrderListItem, "payment"> & {
  email: string | null
  phone: string | null
  boxnow: BoxNowLines
  address: { line: string | null; city: string | null; postcode: string | null }
  /** `extra_for`: on an extras line (the packaging), the item it was chosen for. */
  items: Array<{
    id: string
    title: string
    variant: string | null
    thumbnail: string | null
    quantity: number
    unit_price: number
    fulfilled: number
    extra_for?: string | null
    variant_id?: string | null
    /** Units that left the shop and have not come back: what a return may bring back. */
    returnable?: number
    /** What one unit was paid, after its share of a discount code. */
    paid_unit_price?: number
  }>
  /** What the order charged for delivery (a full withdrawal gets it back too). */
  delivery_charged?: number
  /** Returns and exchanges recorded in HQ, oldest first. */
  returns?: OrderReturn[]
  shipping: Array<{ name: string | null; amount: number }>
  fulfillments: Array<{
    id: string
    created_at: string | null
    shipped_at: string | null
    delivered_at: string | null
    canceled_at: string | null
    tracking: Array<{ number: string; url: string | null }>
  }>
  payment: {
    kind: "card" | "cash" | "unknown"
    collected: boolean
    refunded: number
    can_collect_cash: boolean
    /** What a refund may still give back, in euros. */
    refundable: number
    refunds: Array<{ id: string | null; amount: number; created_at: string | null; note: string | null; emailed: boolean }>
  }
  discount: { total: number; codes: string[] }
  can_ship: boolean
  can_hand_over: boolean
  can_cancel: boolean
  /** Units taken out as out of stock from HQ (Medusa drops a line taken out whole). */
  removed?: Array<{ title: string; units: number; at: string; to?: string | null }>
  /** The delivery charge changed (or taken off) from HQ, oldest first. */
  delivery_changes?: Array<{ from: number; to: number; at: string; note: string | null }>
  /** The emails the customer has had (backend lib/hq/order-emails.ts), and the resends from HQ. */
  emails?: OrderEmail[]
  email_resends?: EmailResend[]
}

export type OrderEmailType = "confirmation" | "shipped" | "delivered" | "canceled" | "refunded" | "returned" | "exchange_sent"
export type OrderEmail = { type: OrderEmailType; id: string; at: string | null; amount?: number }
export type EmailResend = { request_id: string; type: OrderEmailType; id: string; n: number; at: string; by: string | null; to: string | null; mode: "live" | "owner_only" }

/** Free delivery from an order value up, per paid delivery method (backend lib/hq/free-delivery.ts). */
export type FreeDeliveryMethod = "home" | "box_now"
export type FreeDeliveryOption = { name: string; price: number | null; threshold: number | null }
export type FreeDeliverySettings = { options: Record<FreeDeliveryMethod, FreeDeliveryOption | null>; limits: { min: number; max: number } }


export type ChannelKey = "web" | "shopify" | "custom"
type Money = { revenue: number; orders: number }
type MonthMoney = Money & { profit: number; costed_revenue: number }
/** GET /admin/hq/analytics/insights — customers, custom demand and products (backend lib/hq/insights.ts). */
export type InsightsReport = {
  today: string
  customers: {
    totals: {
      customers: number
      repeat_customers: number
      repeat_rate: number | null
      orders_per_customer: number | null
      repeat_revenue_share: number | null
      days_to_second_order: number | null
      attica_share: number | null
    }
    by_month: Array<{ month: string; new_customers: number; returning_customers: number; new_revenue: number; returning_revenue: number }>
    cohorts: Array<{ month: string; customers: number; again_90: number | null; again_180: number | null; revenue_per_customer: number | null }>
    top_customers: Array<{ code: string; orders: number; revenue: number; first: string; last: string; city: string | null }>
    cities: Array<{ city: string; orders: number; revenue: number }>
  }
  custom: {
    totals: { orders: number; orders_with_handle: number; customers: number; repeat_customers: number; repeat_rate: number | null }
    top_customers: Array<{ handle: string; orders: number; revenue: number; profit: number; first: string; last: string }>
    wanted: Array<{
      key: string
      label: string
      items: number
      orders: number
      from_stock: number
      average_sale: number | null
      margin: number | null
      last: string
      in_shop: string[]
      top: Array<{ name: string; n: number }>
      sizes: Array<{ size: string; n: number }>
    }>
  }
  products: {
    hot: Array<{ key: string; id: string | null; title: string; category: string; units_30: number; units_before: number; pace: number | null; in_shop: boolean; available: number | null }>
    slow: Array<{
      id: string | null
      handle: string
      title: string
      category: string
      stock: number
      price: number | null
      cost: number | null
      stock_value: number | null
      last_sold: string | null
      days_since_sale: number | null
      days_listed: number | null
      sold_90: number
      cover_days: number | null
      floor_price: number | null
      reason: "never_sold" | "no_recent_sale" | "too_much_stock"
    }>
    sizes: Array<{ category: string; units: number; sizes: Array<{ size: string; units: number; share: number }> }>
  }
}

/** GET /admin/hq/stock/outlook: when sizes run out, what running out cost, returns (backend lib/hq/stock-outlook.ts). */
export type StockOutlook = {
  today: string
  missed_since: string | null
  returns_since: string
  products: Array<{
    key: string
    title: string
    category: string
    rate: number | null
    price: number | null
    sizes: Array<{ option: string; stock: number; per_day: number | null; days_left: number | null; runs_out: string | null; sold_out_days: number; missed_units: number }>
    runs_out: string | null
    runs_out_size: string | null
    missed: { units: number; value: number }
    returns: { units: number; sold: number; rate: number | null; reasons: Record<string, number> }
  }>
  missed: { units: number; value: number }
  returns: {
    units: number
    sold: number
    rate: number | null
    reasons: Array<{ reason: string; label: string; units: number }>
    by_category: Array<{ category: string; returned: number; sold: number; rate: number | null }>
  }
}

/** GET /admin/hq/analytics/pulse: sales made (placed, not cancelled) in Today's windows (backend lib/hq/pulse.ts). */
export type PulseMoney = { revenue: number; orders: number }
export type PulseWindow = { from: string; to: string; all: PulseMoney; site: PulseMoney; custom: PulseMoney }
export type Pulse = { day: string; today: PulseWindow; yesterday: PulseWindow; week: PulseWindow; month: PulseWindow; last_month_same_days: PulseWindow }

export type SalesReport = {
  from: string | null
  to: string | null
  channels: ChannelKey[]
  totals: {
    revenue: number
    orders: number
    units: number
    aov: number
    customers: number
    repeat_customers: number
    /** Of the period's customers, those who had bought before it; null for all time. */
    returning_customers: number | null
    canceled: number
    open: number
    /** Over the orders whose cost is known only (custom orders, so far). */
    profit: number
    profit_orders: number
    margin: number | null
    profit_coverage: number
  }
  by_month: Array<{ month: string } & Record<ChannelKey, MonthMoney>>
  top_products: Array<{ key: string; title: string; category: string; units: number; revenue: number; margin: number | null; cost_coverage: number; sizes: Record<string, number> }>
  by_category: Array<{ key: string; revenue: number; units: number }>
  by_channel: Array<{ key: string } & Money>
  by_delivery: Array<{ key: string } & Money>
  by_payment: Array<{ key: string } & Money>
  quality: { assumed_paid_revenue: number; booked_revenue: number; estimated_cost_orders: number; cost_coverage: number; custom_payments: number }
  explain: SalesExplain
  expenses: PeriodExpenses
}

/** How each Sales card was worked out (lib/hq/sales-analytics.ts explainReport). */
export type SalesExplain = {
  revenue: {
    by_channel: Array<{ channel: ChannelKey; orders: number; revenue: number; items: number; delivery_charged: number; discounts: number; adjustments: number; refunds: number }>
    by_status: Array<{ status: "paid" | "assumed_paid" | "booked"; orders: number; revenue: number }>
    left_out: Record<"canceled" | "open" | "custom_payments", { orders: number; value: number }>
  }
  profit: {
    by_channel: Array<{ channel: ChannelKey; orders: number; costed_orders: number; costed_revenue: number; goods: number; delivery_cost: number; packaging: number; fees: number; profit: number; estimated_cost_orders: number }>
    uncosted: { orders: number; revenue: number }
  }
  customers: { with_key: number; orders_without_customer: number; returning: number | null; new: number | null; more_than_once_in_period: number }
}

/** The business's running costs for the period, from the ledger (lib/hq/finance/period-expenses.ts). */
export type PeriodExpenses = {
  from: string
  to: string
  total: number
  categories: Array<{ key: string; label: string; ledger: number; fixed: number; lines: number; amount: number }>
  fixed: Array<{ category: string; label: string; monthly: number; days: number; month_days: number; amount: number; month: string }>
  lines: Array<{ day: string; text: string; amount: number; category: string | null; counted: "running" | "unclassified" | "replaced_by_fixed" }>
  in_order_cost: number
  not_expense: number
  income: number
  ledger_lines: number
  ledger_last_day: string | null
  /** Every payment the ledger shows in these days, Larry included (what was PAID, not what was SOLD). */
  cash_out: {
    total: number
    larry: number
    delivery: number
    running: number
    refunds: number
    moved_to_partner: number
    categories: Array<{ key: string; label: string; amount: number; lines: number; group: string }>
  }
  /** The goods cost of the orders sold in these days (inside profit), to set beside what Larry was paid. */
  goods_sold: { comparable: boolean; site: number; custom: number; total: number; uncosted_orders: number }
}

export type CostRule = {
  id: string
  match: string
  yuan: number | null
  eur: number | null
  extra_eur: number
  valid_from: string | null
  estimated: boolean
  note: string | null
  source: string
  unit_cost: number | null
  units: number
  revenue: number
}
export type CostRuleInput = Pick<CostRule, "match" | "yuan" | "eur" | "extra_eur" | "valid_from" | "estimated" | "note">
export type SiteProduct = { key: string; title: string; category: string; units: number; revenue: number; last_day: string; rule_id: string | null; rule_source: string | null; unit_cost: number | null }
export type LarryStock = {
  batches: Array<{ day: string; qty: number; yuan_total: number; yuan_each: number; source: string; freight_basis: string; eur_goods: number; eur_freight: number; eur_each: number; rate_basis: string }>
  items: number
  matches: Array<{ title: string; revenue: number; items: Array<{ text: string; day: string | null; yuan_each: number; eur_each: number; source: string }> }>
  unmatched: Array<{ title: string; revenue: number }>
  rules: number
  revenue: number
  rules_in_use: number
  checked_at?: string
  synced_at: string
}
export type CostsReport = {
  rate: number
  rate_from_top_ups: boolean
  delivery_costs: Record<string, number | null>
  elta: { prepaid: number; cash_on_delivery: number }
  meetup_bag: number
  card_fee: { percent: number; fixed: number }
  stock_freight: {
    yuan_per_kg: number | null
    yuan_per_estimated_kg: number | null
    bulk_parcels: number
    stock_freight_yuan: number
    stock_freight_eur: number
    unlinked_parcels: number
    unlinked_yuan: number
  } | null
  coverage: { revenue: number; costed_revenue: number; share: number; by_owner: number; by_larry: number }
  rules: CostRule[]
  larry: LarryStock | null
  products: SiteProduct[]
}
export type DeliveryReport = {
  months: number
  from: string
  orders: number
  delivery: Array<{
    method: "box_now" | "meetup" | "home" | "elta" | "unknown"
    orders: number
    charged: number
    cost: number
    net: number
    cost_known: boolean
    per_order: { charged: number; cost: number; net: number } | null
    today: { charge: number; cost: number; net: number } | null
  }>
  payments: Array<{ payment: string; orders: number; revenue: number; fees: number; real_fees: number; per_order: number; share: number }>
}
type SoldCounts = { d30: number; d90: number; d365: number; all: number }
type ProductHistory = { sold: SoldCounts; revenue_365: number; revenue: number; average_price: number | null; realized_margin: number | null; last_sold: string | null }
export type ProductProfit = {
  today: string
  charges: Partial<Record<"box_now" | "meetup" | "home", number>>
  totals: { products: number; in_stock: number; sold_out: number; units: number; stock_cost: number; stock_price: number; uncosted_units: number }
  products: Array<
    ProductHistory & {
      id: string | null
      handle: string
      title: string
      category: string
      status: string
      price: number | null
      price_max: number | null
      cost: number | null
      cost_source: "yours" | "larry" | null
      margin: number | null
      per_method: { box_now: number | null; meetup: number | null; home: number | null } | null
      stock: number | null
      sizes: Array<{ variant_id: string | null; title: string; available: number | null; stocked: number | null; reserved: number | null }>
      stock_cost: number | null
      stock_price: number | null
      cover_days: number | null
    }
  >
  gone: Array<ProductHistory & { handle: string; title: string; category: string; sizes?: Array<{ size: string; units: number }> }>
}
export type PlannerReport = {
  today: string
  defaults: { budget: number; horizon_days: number; lead_days: number; unsold_loss: number; product_cap: number }
  /** Days from Larry having an order to it being here, measured on custom orders (from 5 up); null = the 21-day default. */
  lead_measured?: { n: number; p50: number; p80: number } | null
  selling_cost_per_unit: number
  candidates: import("./planner").PlannerCandidate[]
  skipped: Array<{ title: string; reason: string }>
}
/** HQ → Stock age (backend lib/hq/stock/lots-report.ts). */
export type MarkdownReason = { code: "oldest"; days: number } | { code: "no_sale"; days: number } | { code: "never_sold" } | { code: "cover"; days: number } | { code: "sell_through"; pct: number }
export type StockLotsReport = {
  today: string
  history_from: string | null
  totals: { units: number; value_fifo: number; value_today: number; uncosted_units: number; unexplained_in: number; unexplained_out: number }
  age_buckets: Array<{ key: "0_30" | "31_60" | "61_90" | "91_180" | "181_plus"; units: number; value: number }>
  products: Array<{
    id: string | null
    handle: string
    title: string
    category: string
    price: number | null
    cost: number | null
    units: number
    value: number | null
    uncosted_units: number
    oldest_days: number | null
    oldest_estimated: boolean
    sold_30: number
    sold_60: number
    last_sale_days: number | null
    cover_days: number | null
    sell_through_60: number | null
    sizes: Array<{ variant_id: string; title: string; units: number; oldest_days: number | null }>
  }>
  markdowns: Array<{
    id: string | null
    handle: string
    title: string
    units: number
    price: number | null
    cost: number | null
    oldest_days: number | null
    oldest_estimated: boolean
    suggested: number | null
    floor: number | null
    discount: number
    reasons: MarkdownReason[]
    room: boolean
  }>
  batches: Array<{ key: string; kind: "opening" | "received"; at: string; units: number; remaining: number; products: number; value: number; sell_through: Record<"d7" | "d14" | "d30" | "d60", number | null> }>
}
export type ProductMerge = { id: string; from_key: string; to_key: string; to_title: string; note: string | null }
export type BulkOrder = {
  products: Array<{ name: string; link: string; qty: number; yuanPerUnit: number; eurPerUnit: number; kind: "cap" | "bag" }>
  capCount: number
  capsCostYuan: number
  capUnitYuan: number
}

export type ExpenseCategory =
  | "larry"
  | "delivery_person"
  | "box_now"
  | "meetup_bags"
  | "refunds"
  | "order_money"
  | "staff"
  | "marketing"
  | "software"
  | "shopify"
  | "consumables"
  | "couriers"
  | "other"
export type RunningKey = "staff" | "marketing" | "software" | "shopify" | "consumables" | "couriers" | "other"
export type PnlMonth = {
  month: string
  revenue: { site: number; custom: number; total: number }
  costed_revenue: { site: number; custom: number }
  goods: number
  selling: number
  gross_profit: number | null
  gross_profit_costed: number
  gross_profit_estimated: number
  estimated_share: number
  running: Record<RunningKey | "unclassified" | "total", number>
  operating_profit: number | null
  fixed: Partial<Record<ExpenseCategory, number>>
  in_order_cost: Partial<Record<ExpenseCategory, number>>
  not_expense: Partial<Record<ExpenseCategory, number>>
  cash: { in: number; out: number; net: number }
  has_ledger: boolean
  missing: string | null
}
export type FixedCost = { id: string; category: ExpenseCategory; amount: number; from_month: string; until_month: string | null; note: string | null }
export type MoneyReport = {
  months: PnlMonth[]
  fixed_costs: FixedCost[]
  categories: Record<ExpenseCategory, { label: string; group: "in_order_cost" | "not_expense" | "running"; ended?: string }>
  to_classify: { lines: number; amount: number }
  ledger: { entries: number; first: string | null; last: string | null; checked_at?: string; synced_at: string } | null
  /** Each month's last "money tied up" reading (its close). */
  month_ends: Record<string, { day: string; data: TiedUpNow }>
  first_snapshot_day: string | null
}
/** Where the money that is not in the bank sits (lib/hq/finance/tied-up.ts). */
export type TiedUpNow = {
  in_goods: {
    total: number
    stock_on_shelf: { cost: number; price: number; units: number; uncosted_units: number }
    stock_on_the_way: {
      cost: number
      units: number
      orders: number
      uncosted_units: number
      /** Left to receive on HQ's purchase orders (older readings lack it). */
      from_orders?: number
      /** Stock in Larry's shipments, not on the shelf yet. */
      from_larry?: {
        cost: number
        units: number
        shipments: Array<{
          key: string
          label: string
          day: string | null
          status: string
          cost: number
          lines: Array<{ text: string; yuan: number | null; qty: number | null; eur: number | null; basis: "batch" | "goods" | "no_price" }>
        }>
        left_out: Array<{ text: string; shipment: string }>
      }
    }
    custom_paid_to_larry: { amount: number; orders: number; open: number; from_stock: number }
    larry_credit: number
  }
  coming_in: { total: number; custom_balances: { amount: number; orders: number }; site_cash: { amount: number; orders: number } }
  larry_owed: number
  larry: { yuan: number | null; eur: number | null; rate: number }
  deposits_held: number
  custom_fronted: number
  site_not_handed_over: { amount: number; orders: number }
  net: number
}
export type TiedUpReport = {
  day: string
  now: TiedUpNow
  fronting: Array<{ order_number: string; stage: string; paid_to_larry: number; deposit: number; to_collect: number; fronted: number }>
  larry_read_at: string | null
  history: Array<{ day: string; net: number | null; in_goods: number | null; coming_in: number | null; larry_owed: number | null }>
}
export type CustomBank = {
  typical_landed_share: number
  open: { orders: number; sale: number; deposits: number; no_deposit: number; paid_to_larry: number; to_collect: number }
  coverage: { orders: number; sale: number; deposits: number; landed: number; deposit_share: number | null; landed_share: number | null; under_covered: number; gap: number }
  fronted: { orders: number; amount: number }
  stale: {
    orders: number
    to_collect: number
    paid_to_larry: number
    list: Array<{ order_number: string; stage: string; stage_label: string; age_days: number | null; to_collect: number; paid_to_larry: number }>
  }
  cancellations: { finished: number; cancelled: number; rate: number | null; after_larry: { orders: number; paid_to_larry: number; deposits: number; uncovered: number } }
  stages: Array<{ stage: string; label: string; orders: number; deposits: number; paid_to_larry: number; to_collect: number }>
  orders: Array<{
    order_number: string
    handle: string
    stage: string
    age_days: number | null
    stale: boolean
    sale: number
    deposit: number
    landed: number
    paid_to_larry: number
    fronted: number
    to_collect: number
    suggested: number | null
    short: number | null
  }>
}
export type LarryKind = "top_up" | "refund" | "goods" | "shipping" | "other"
export type LarryReport =
  | { available: false }
  | {
      available: true
      read_at: string | null
      rate: number
      balance: {
        yuan: number
        eur: number
        larry: { yuan: number; day: string | null } | null
        after: Array<{ day: string | null; kind: LarryKind; yuan: number }>
        computed: number
        drift: number
        slips: Array<{ row: number; day: string | null; change: number }>
      }
      months: Array<{
        month: string
        top_up_yuan: number
        top_up_eur: number
        eur_estimated: number
        top_ups: number
        refunds_yuan: number
        goods_yuan: number
        stock_batch_yuan: number
        shipping_yuan: number
        other_yuan: number
        balance_end_yuan: number
        ledger_eur: number
      }>
      ledger_from: string | null
      recent: Array<{ day: string | null; kind: LarryKind; yuan: number; eur: number | null; note: string; row: number }>
    }
export type StockInvestments = {
  today: string
  history_start: string | null
  batches: Array<{
    day: string
    source: string
    qty: number
    eur_each: number
    cost: number
    sold: number
    left: number
    left_cost: number
    revenue: number
    back: number | null
    first_sale: string | null
    paid_back: string | null
    days_to_pay_back: number | null
    sold_out: string | null
    days_to_sell_out: number | null
    age_days: number
    empty_before_days: number | null
    before_history: boolean
  }>
  outside: { units: number; revenue: number }
  totals: { batches: number; qty: number; cost: number; revenue: number; left: number; left_cost: number; paid_back: number; median_days_to_pay_back: number | null }
  slow: {
    products: number
    units: number
    stock_value: number
    uncosted: number
    list: Array<{ handle: string; title: string; stock: number; stock_value: number | null; days_since_sale: number | null; reason: string; floor_price: number | null; price: number | null }>
  }
}
export type LedgerLine = {
  key: string
  day: string
  kind: "income" | "expense"
  amount: number
  text: string
  category: ExpenseCategory | null
  basis: "by hand" | "your rule" | "HQ rule" | "none"
  match: string | null
}

export type ForecastGroup = { orders: number; revenue: number; costed_revenue: number; profit: number }
export type OpenStage = "to_hand_over" | "in_greece" | "on_the_way" | "ordered" | "from_stock" | "not_sent"
export type GroupBaseline = {
  months: number
  orders_3m: number
  orders_6m: number
  orders_12m: number
  revenue_3m: number
  aov: number | null
  margin: number | null
  margin_basis: string
  margin_coverage: number
}
export type PipelineStageSummary = {
  stage: OpenStage
  label: string
  days: [number, number]
  /** How many orders the days were measured on (backend lib/hq/custom/lead-times.ts); null = estimated. */
  measured?: number | null
  orders: number
  stale: number
  sale: number
  balance: number
  expected_balance: number
  to_pay: number
  expected_to_pay: number
  profit: number
  expected_profit: number
}
export type PipelineOrder = {
  order_number: string
  handle: string
  athens_date: string | null
  age_days: number | null
  stage: OpenStage
  stale: boolean
  sale: number
  deposit: number
  balance: number
  to_pay: number
  profit: number | null
  chance: number
  no_price: boolean
}
export type ForecastReport = {
  today: string
  this_month: string
  history: Array<{ month: string; site: ForecastGroup; custom: ForecastGroup; running: number | null; gross_profit: number | null; operating_profit: number | null }>
  month_to_date: {
    month: string
    day: number
    days: number
    site: ForecastGroup & { run_rate_revenue: number; run_rate_orders: number }
    custom: ForecastGroup & { run_rate_revenue: number; run_rate_orders: number }
  }
  groups: { site: GroupBaseline; custom: GroupBaseline }
  band: { low: number; high: number; samples: number; basis: string }
  running: {
    lines: Array<{ key: RunningKey | "unclassified"; label: string; amount: number; basis: string }>
    total: number
    left_out: Array<{ key: RunningKey; label: string; amount: number; ended: string }>
    months: string[]
  }
  coming: Array<{ month: string; last_year: { site: number; custom: number; orders: number } | null }>
  pipeline: {
    cancel_rates: { not_shipped: number; shipped: number; finished: number; finished_shipped: number }
    totals: {
      orders: number
      stale: number
      no_price: number
      no_cost: number
      sale: number
      deposits: number
      balance: number
      expected_balance: number
      to_pay: number
      expected_to_pay: number
      profit: number
      expected_profit: number
    }
    stages: PipelineStageSummary[]
    orders: PipelineOrder[]
  }
  stage_history: { events: number; since: string | null; lead_times?: { arrival: { n: number; p50: number; p80: number } | null } }
}

export type CustomStatus = "pending" | "active" | "completed" | "cancelled"
export type CustomReview =
  | "price_unclear"
  | "no_cost"
  | "bulk_parcel"
  | "multi_row"
  | "repeated_row"
  | "no_date"
  | "date_estimated"
  | "future_date"
  | "continuation"
  | "price_unusual"
  | "below_cost"

export type CustomSync = {
  orders: number
  by_status: Record<CustomStatus, number>
  needs_review: number
  review: Partial<Record<CustomReview, number>>
  revenue: number
  profit: number
  costed_orders: number
  larry_balance_yuan: number | null
  larry_balance_raw: string | null
  larry_error: string | null
  parcels: number
  rate: number
  checked_at?: string
  synced_at: string
}

export type CustomOrderListItem = {
  order_number: string
  handle: string
  athens_date: string | null
  date_estimated: boolean
  status: CustomStatus
  delivery: "box_now" | "home" | "meetup" | "elta" | "unknown"
  prepay_status: string
  larry_status: string
  items: Array<{ title: string; size: string | null; category: string }>
  sale_total: number | null
  profit: number | null
  shipping_source: "estimate" | "actual"
  review: CustomReview[]
  is_stock: boolean
  parcel_status: string | null
  /** List only: what the filters read (see lib/custom-filters.ts). */
  sent_to_larry?: boolean
  hq_delivery?: "box_now" | "home" | "meetup" | "elta" | null
  contacted_at?: string | null
  handed_over_at?: string | null
  left?: number | null
  settled?: boolean
}

export type CustomOrderDetail = Omit<CustomOrderListItem, "items"> & {
  sheet_rows: Array<{ tab: string; row: number }>
  date_raw: string
  shipping_raw: string
  shipping_inherited: boolean
  prepay_amount: string
  prepay_method: string
  supplier: string
  tracking: string
  customer_info: string
  /** `oos`: Larry could not buy it (marked «OUT OF STOCK» in the sheet). */
  items: Array<{ en: string; zh: string; link: string; title: string; size: string | null; category: string; yuan: number | null; cost_eur: number | null; sale_eur: number | null; oos?: boolean }>
  cost_total: number | null
  shipping_est: number
  shipping_used: number
  local_delivery: number
  parcel_tracking: string | null
  costing: CustomCosting | null
}

/** HQ's cost breakdown for one custom order, and the partner's app's figures beside it. */
export type CustomCosting = {
  rate: number
  rate_basis: "top-ups" | "nearest top-up" | "default"
  goods: number | null
  freight: number
  freight_method: "parcel" | "parcel+estimate" | "estimate"
  weight_kg: number
  item_kg: number[]
  parcel: { tracking: string; kg: number; share_kg: number; bulk: boolean } | null
  delivery: number
  delivery_known: boolean
  packaging: number
  fee: number
  fee_basis: string
  profit: number | null
  quality: "confirmed" | "estimated" | "missing"
  partner?: { goods: number | null; freight: number; delivery: number; profit: number | null }
  /** An owner used a manual correction: what the sale and goods were before it. */
  corrected?: { sale_from: number | null; cost_from: number | null }
}

/** A manual correction of a custom order: from the partner's platform (its export) or made in HQ. */
export type CustomCorrection = {
  id: string
  order_number: string
  sale_total_eur: number | null
  cost_total_eur: number | null
  note: string | null
  source: "platform" | "hq"
  state: "pending" | "applied" | "dismissed" | "matches"
  source_updated_at: string | null
  decided_at: string | null
}
export type CorrectionRow = CustomCorrection & {
  order: { handle: string; sale_total: number | null; goods: number | null; profit: number | null; partner_goods: number | null; corrected: boolean; first_item: string | null } | null
}
/** Which sheet HQ's writes go to: the partner's TEST copy, or the real ORDERS sheet. */
export type BridgeTarget = "test" | "live"
export type CustomOrderActivity = {
  id: string
  action: string
  from: string | null
  to: string | null
  target: BridgeTarget
  ok: boolean
  detail: {
    tab?: string
    row?: number
    took_ms?: number
    error?: string
    /** Cells the read-back found different from what HQ sent (a dropdown refused a value, say). */
    mismatches?: SheetMismatch[]
    recovered?: boolean
    already_sent?: boolean
    larry_rows?: number | null
    sent_to_larry?: boolean
    /** The call HQ made to n8n in place of the sheet's own (backend lib/hq/custom/n8n.ts). */
    n8n?: { sent: boolean; status: number | null; column: "PREPAYMENT" | "ORDERSTATUS" | null; error: string | null; skipped?: string }
  } | null
  by: string | null
  at: string
}
export type SheetField = "handle" | "en" | "yuan" | "link" | "sale" | "shipping" | "prepay" | "prepay_status" | "larry" | "number" | "prepay_method" | "customer_info"
export type SheetMismatch = { field: SheetField; expected: string; shown: string }
export type CustomIntakeItem = { en: string; zh?: string; pick_id?: string; link: string; yuan: number | null; eur: number | null; images?: string[] }
export type NewCustomOrderBody = {
  handle: string
  shipping: string
  shipping_note?: string
  items: CustomIntakeItem[]
  discount?: number
  prepay_amount: number | null
  prepay_method: string
  prepay_status: "PAID" | "NOT PAID"
  customer_info?: string
  send_to_larry?: boolean
  /** Every item's photo combined into one picture, for the order's one IMAGE cell. */
  image?: string | null
}
export type CustomNewContext = {
  bridge: { target: BridgeTarget; larry: boolean } | null
  choices: { shipping: string[]; methods: string[]; statuses: string[] }
  suppliers: Array<{ id: string; title: string; url: string; category: string | null; size_guide: string | null; size_guide_images: string[] | null }>
}
export type CustomPreview = {
  rate: number
  lines: Array<{ category: string; kg: number; goods: number | null; freight: number | null; sale: number | null; discount: number; profit: number | null }>
  gross: number
  discount: number
  net: number
  goods: number
  freight: number
  delivery: number | null
  delivery_known: boolean
  bag: number
  fee: number
  fee_basis: string
  profit: number | null
  margin: number | null
  complete: boolean
  suggested_deposit: number | null
}
export type CustomCreated = {
  order_number: string
  target: BridgeTarget
  tab: string
  row: number
  mismatches: SheetMismatch[]
  sent_to_larry: boolean
  larry_rows: number | null
  image_status: string | null
  took_ms: number
  recovered: boolean
  replay: boolean
  synced: boolean
}
export type CustomWritten = {
  order_number: string
  kind: "details" | "items" | "paid" | "larry" | "prices"
  target: BridgeTarget
  tab: string
  row: number
  mismatches: SheetMismatch[]
  took_ms: number
  changed: string[]
  already_sent?: boolean
  larry_rows?: number | null
  synced: boolean
  /** Mark PAID only: the contacted order went to «To deliver» straight after. */
  moved?: boolean
}
export type CustomDetailsBody = { handle?: string; shipping?: string; shipping_note?: string; prepay_amount?: number | null; prepay_method?: string; prepay_status?: "PAID" | "NOT PAID"; customer_info?: string }
/** An order HQ created on the partner's TEST copy: the mirror (the real sheet) never has it. */
export type CustomTestOnlyPage = {
  test_only: true
  intake: { order_number: string; target: BridgeTarget; row: number | null; created_at: string; order: NewCustomOrderBody & { sent_to_larry?: boolean } }
  bridge: { target: BridgeTarget; larry: boolean } | null
  activity: CustomOrderActivity[]
}
/** One custom order's page: the order, and everything HQ keeps about it beside the sheet. */
export type CustomOrderPage = {
  order: CustomOrderDetail
  /** Each item's photo (its w960 address), "" for an item without one. */
  item_photos?: string[]
  item_zh?: string[]
  /** What each item was sold for, as HQ remembers it (source "hq"), else the sheet's / the creation's own per-item prices; empty items = nobody ever said. */
  item_sold?: { items: Array<{ eur: number | null; oos: boolean }>; source: "hq" | "sheet" | "intake" | null; at: string | null; by: string | null; history: Array<{ items: Array<{ eur: number | null; oos: boolean }>; at: string; by: string | null; source: string }> }
  correction: CustomCorrection | null
  state: { contacted_at: string | null; delivery: DeliveryChoice | null; handed_over_at: string | null; note: string | null; delay_told_at?: string | null } | null
  received_at: string | null
  /** Problems found in a shipment from Larry that name this order, open ones first. */
  parcel_problems?: ParcelProblem[]
  paid: number
  /** What HQ's paid payment links brought in for this order. */
  paid_by_link: number
  settled: boolean
  left: number | null
  /** The deposit that covers Larry's goods and shipping (or the typical share of the price, before a cost). */
  suggested_deposit: { amount: number; basis: "cost" | "typical"; landed: number; share: number | null; short: number } | null
  links: PaymentLinkView[]
  boxnow: BoxNowLines
  /** Sent with BOX NOW from HQ. */
  shipped: { voucher: string; url: string; at: string | null; by: string | null; emailed_to: string | null; emailed_at: string | null } | null
  bridge: { target: BridgeTarget; larry?: boolean } | null
  activity: CustomOrderActivity[]
  test_only?: undefined
}
export type PaymentLinkKind = "deposit" | "balance" | "other"
/** A Stripe payment link made from HQ ("quick sale"), with the DM to send with it. */
export type PaymentLinkView = {
  id: string
  kind: PaymentLinkKind
  amount: number
  title: string
  order_number: string | null
  handle: string | null
  note: string | null
  status: "open" | "paid" | "cancelled"
  url: string
  paid_at: string | null
  fee: number | null
  checked_at: string | null
  created_at: string
  message: string
  refunded: number
  refunded_at: string | null
  can_refund: boolean
}
export type NewPaymentLink = { request_id: string; kind: PaymentLinkKind; amount: number; title?: string; order_number?: string | null; handle?: string | null; note?: string | null }
export type ReplyTemplate = { id: string; title: string; body: string; sort: number; updated_at: string }
export type SupplierLink = { id: string; title: string; url: string; category: string | null; size_guide: string | null; size_guide_images: string[] | null; updated_at: string }
export type SupplierBody = { title: string; url: string; category: string | null; size_guide?: string | null; size_guide_images?: string[] | null }
export type OrderNote = { id: string; body: string; author: string | null; actor_id: string | null; created_at: string }
export type DiscountState = "active" | "used_up" | "expired" | "revoked" | "draft"
export type Discount = {
  id: string
  code: string | null
  off: string
  type: "percentage" | "fixed" | null
  value: number
  limit: number | null
  used: number
  expires_at: string | null
  state: DiscountState
  automatic: boolean
  created_at: string | null
  reason: "review" | "giveaway" | "hq" | null
  discord_user_id: string | null
  discord_username: string | null
  note: string | null
  made_in: "hq" | "discord" | "medusa"
}
export type NewDiscount = { code?: string | null; type: "percentage" | "fixed"; value: number; limit: number | null; expires_at: string | null; note?: string | null }
export type PlatformImportSummary = {
  templates: { created: number; updated: number; same: number }
  suppliers: { created: number; updated: number; same: number }
  corrections: { to_check: number; matching: number; kept: number }
  parcels: { marked: number; already: number; problems: number }
}
export type ImportSummary = {
  orders: number
  first: string | null
  last: string | null
  by_status: Record<"paid" | "assumed_paid" | "booked" | "open" | "canceled", number>
  custom_payments: number
  net_revenue: number
  lines: number
  matched_lines: number
  lines_with_cost: number
  catalog_products?: number
}

export type ProductSetup = {
  categories: string[]
  ready: { sales_channel: boolean; location: boolean; shipping_profile: boolean }
  cap_category: string
  presets: Record<string, string[]>
  media_origin: string
}
export type CreatedProduct = {
  id: string
  title: string
  handle: string
  status: string
  thumbnail: string | null
  category: string | null
  price: number | null
  sizes: string[]
  stock: number
  problems: string[]
  created_at: string
}
export type NewProductBody = {
  request_id: string
  title: string
  brand: string | null
  category: string
  description: string | null
  price_eur: number
  sizes?: Array<{ value: string; stock: number }>
  one_size_stock?: number | null
  images: string[]
  status: "draft" | "published"
  /** Extras groups it offers (the packaging): ids from GET /admin/hq/extras. */
  extras?: string[]
}
/** A choice beside the size (HQ → Extras), e.g. «Συσκευασία»: Standard free, Premium +5 €. The first choice is the default. */
/** `stock`: at the shop when the choice's stock is tracked (Extras → Track stock), else null. */
export type ExtraChoice = { variant_id: string; label: string; price: number; rank: number; key: string; stock?: { stocked: number; reserved: number; available: number } | null }
export type ExtraGroup = {
  id: string
  title: string
  handle: string
  status: string
  choices: ExtraChoice[]
  products: Array<{ id: string; title: string; handle: string; status: string }>
}
export type ExtraGroupBody = { title: string; choices: Array<{ variant_id?: string | null; label: string; price: number }> }
export type HqAlert = { key: string; kind: string; severity: "urgent" | "today" | "fyi"; title: string; detail: string; link: string | null; since: string; checked_at: string }
/** GET /admin/hq/products/health: the catalogue check (backend lib/hq/products/health.ts). */
export type CatalogHealthIssue = {
  key: "readiness" | "test_on_sale" | "handle_other" | "handle_words" | "same_name" | "draft_stock" | "draft_not_ready"
  level: "sell" | "preview" | "tidy"
  text?: string
  params?: Record<string, string | number>
}
export type CatalogHealthProduct = {
  id: string
  title: string
  handle: string
  status: string
  thumbnail: string | null
  photo: string | null
  photo_width: number | null
  stock: number
  issues: CatalogHealthIssue[]
}

export type ProductListItem = {
  id: string
  title: string
  handle: string
  status: string
  thumbnail: string | null
  category: string | null
  price: number | null
  price_max: number | null
  sizes: string[]
  stock: number
  problems: string[]
  made_in_hq: boolean
  deletable: boolean
}
export type EditableProduct = {
  id: string
  title: string
  handle: string
  status: string
  brand: string | null
  description: string | null
  category: string | null
  images: string[]
  option_title: string | null
  one_size: boolean
  made_in_hq: boolean
  deletable: boolean
  sizes: Array<{ variant_id: string; value: string; price: number | null; stocked: number; reserved: number }>
  /** Extras groups it offers. */
  extras?: string[]
}
export type EditProductBody = {
  request_id: string
  title: string
  brand: string | null
  category: string
  description: string | null
  images: string[]
  sizes: Array<{ variant_id: string; price: number; stock?: number | null }>
  remove_variant_ids: string[]
  new_sizes: Array<{ value: string; price: number; stock: number }>
  extras?: string[]
}
export type StockChangeEntry = {
  request_id: string
  at: string
  kind: "receive" | "adjust" | "count"
  reason: string | null
  note: string | null
  by: string | null
  product_handle: string
  product_title: string
  unit_cost_eur: number | null
  sizes: Array<{ option: string; delta: number; stocked_after: number }>
}
export type StockChangeBody = {
  request_id: string
  kind: "receive" | "adjust" | "count"
  reason?: string | null
  note?: string | null
  unit_cost_eur?: number | null
  lines: Array<{ variant_id: string; quantity: number }>
}

/** GET /admin/hq/purchase-orders — an order to Larry, what has arrived of each line, and its shop product today. */
export type PurchaseOrderStatus = "ordered" | "partly_received" | "received" | "closed"
export type PurchaseOrderLine = {
  id: string
  product_key: string
  title: string
  category: string
  option: string
  quantity: number
  /** Units Larry cannot supply: no longer expected. */
  not_coming?: number
  received: number
  unit_cost_eur: number | null
  price_eur: number | null
  larry_model: string | null
  larry_link: string | null
  product_id: string | null
  product_title: string | null
  product_status: string | null
  product_handle: string | null
  variant_id: string | null
  problem: "not_a_product" | "no_size" | null
}
/** GET /admin/hq/shop-order: one product of the shop, in the order the shop shows it. */
export type ShopOrderProduct = { id: string; handle: string; title: string; category: string; thumbnail: string | null; created_at: string | null; price: number | null; in_stock: boolean; new: boolean }
export type PurchaseOrder = {
  id: string
  ref: string
  number: number
  supplier: string
  note: string | null
  ordered_on: string
  closed_at: string | null
  status: PurchaseOrderStatus
  units: number
  not_coming?: number
  received: number
  estimate: number | null
  estimate_complete: boolean
  lines: PurchaseOrderLine[]
}
export type NewPurchaseOrderLine = Pick<PurchaseOrderLine, "product_key" | "title" | "category" | "option" | "quantity" | "unit_cost_eur" | "price_eur" | "larry_model" | "larry_link">
export type NewPurchaseOrderBody = { request_id: string; supplier?: string | null; note?: string | null; lines: NewPurchaseOrderLine[] }
export type ReceivePurchaseBody = { request_id: string; note?: string | null; lines: Array<{ line_id: string; quantity: number; unit_cost_eur: number | null }> }

/** GET /admin/hq/parcels — Larry's shipments (backend lib/hq/parcels/shipments.ts). */
export type DeliveryChoice = "box_now" | "meetup" | "home" | "elta"
/** Something wrong in a shipment (backend lib/hq/parcels/problems.ts): kept until sorted with Larry. */
export type ParcelProblem = {
  id: string
  /** The shipment it was reported on (its page opens by this key). */
  key: string
  orders: string[]
  stock: boolean
  note: string | null
  status: "open" | "sorted"
  reported_at: string
  sorted_at: string | null
  sorted_note: string | null
  source: string
}
/** What a shipment's customer orders made, by HQ's costing (their share of the freight included). */
export type ShipmentProfit = { orders: number; costed: number; without_cost: number; estimated: number; sales: number; goods: number; freight: number; delivery: number; fees: number; profit: number; margin: number | null }
export type ShipmentOrder =
  | { number: string; missing: true; contacted_at: string | null; handed_over_at: string | null; choice: DeliveryChoice | null; note: string | null; in_problem: boolean }
  | {
      number: string
      missing: false
      handle: string
      day: string | null
      stage: string
      sheet_status: string
      delivery: string
      shipping_raw: string
      choice: DeliveryChoice | null
      contacted_at: string | null
      handed_over_at: string | null
      note: string | null
      total: number | null
      paid: number
      paid_by_link: number
      left: number | null
      prepay: string
      items: Array<{ text: string; size: string | null }>
      customer_info: string
      boxnow: BoxNowLines
      voucher: string | null
      is_stock: boolean
      profit: number | null
      cost_quality: string | null
      in_problem: boolean
    }
export type Shipment = {
  key: string
  label: string
  day: string | null
  status: "on_the_way" | "in_greece" | "received" | "done"
  note: string
  parcels: Array<{
    tracking: string
    kg: number | null
    yuan: number | null
    status: string | null
    received_at: string | null
    /** What DHL last said (null until the DHL key is set, or before DHL knows the parcel). */
    latest: { status: string; code: string | null; location: string | null; at: string | null; delivered: boolean; checked_at: string; events: Array<{ at: string | null; status: string; location: string | null }> } | null
  }>
  weight_kg: number | null
  yuan: number | null
  received_at: string | null
  photos: Array<{ sha: string; ext: string }>
  orders: ShipmentOrder[]
  stock: Array<{ text: string; yuan: number | null; note: string }>
  /** The open problem, else the one sorted last. */
  problem: ParcelProblem | null
  profit: ShipmentProfit
  counts: { orders: number; open: number; contacted: number; handed_over: number; problem: boolean }
}
export type ParcelsReport = {
  checked_at: string | null
  photos: { configured: boolean; exported_at: string | null; error: string | null }
  tracking: { configured: boolean }
  open: Shipment[]
  done: Shipment[]
  waiting_for_stock: Array<{ number: string; handle: string; day: string | null; items: Array<{ text: string; size: string | null }> }>
}
export type CustomOrderStateBody = { contacted?: boolean; delivery?: DeliveryChoice | null; handed_over?: boolean; note?: string | null; delay_told?: boolean }
/** After Contacted (or a payment): whether the order went to «To deliver» by itself. */
export type ContactedResult = { moved?: boolean; waiting_payment?: boolean; move_failed?: string | null }

const post = <T>(path: string, body?: unknown) =>
  sdk.client.fetch<T>(path, { method: "POST", body: body ?? {} })

export const hqApi = {
  health: () => sdk.client.fetch<HqHealth>("/admin/hq/health"),
  push: () => sdk.client.fetch<PushState>("/admin/hq/push"),
  checks: (days: number) => sdk.client.fetch<ChecksReport>(`/admin/hq/analytics/checks?days=${days}`),
  costSettings: () => sdk.client.fetch<{ settings: CostSettings; defaults: CostSettings; limits: Record<keyof CostSettings, [number, number]> }>("/admin/hq/settings/costs"),
  saveCostSettings: (body: { settings?: CostSettings; reset?: boolean }) => post<{ settings: CostSettings; resynced: boolean }>("/admin/hq/settings/costs", body),
  subscribePush: (subscription: PushSubscriptionJSON, label: string, topics: PushTopic[]) =>
    post<{ id: string }>("/admin/hq/push/subscriptions", { endpoint: subscription.endpoint, keys: subscription.keys, label, topics }),
  pushTopics: (id: string, topics: PushTopic[]) => post<{ id: string }>(`/admin/hq/push/subscriptions/${id}`, { topics }),
  removePush: (id: string) => sdk.client.fetch<{ id: string }>(`/admin/hq/push/subscriptions/${id}`, { method: "DELETE" }),
  testPush: () => post<{ sent: number; removed: number; failed: number; devices: number }>("/admin/hq/push/send-test"),
  orders: () => sdk.client.fetch<{ orders: OrderListItem[]; counts: Record<Queue, number> }>("/admin/hq/orders"),
  order: (id: string) => sdk.client.fetch<{ order: OrderDetail }>(`/admin/hq/orders/${id}`),
  ship: (id: string, trackingNumber: string, notify: boolean) =>
    post<{ order: OrderDetail; result: { already_shipped: boolean } }>(`/admin/hq/orders/${id}/ship`, { tracking_number: trackingNumber, notify }),
  handOver: (id: string, collectCash: boolean, notify: boolean) =>
    post<{ order: OrderDetail }>(`/admin/hq/orders/${id}/hand-over`, { collect_cash: collectCash, notify }),
  cancel: (id: string, notify: boolean) => post<{ order: OrderDetail }>(`/admin/hq/orders/${id}/cancel`, { notify }),
  collectCash: (id: string) => post<{ order: OrderDetail }>(`/admin/hq/orders/${id}/collect-cash`),
  removeOrderItem: (id: string, body: { line_item_id: string; units: number; request_id: string; refund: boolean; notify: boolean; swap?: { variant_id: string; price: "new" | "same" } | null }) =>
    post<{ order: OrderDetail; replay: boolean; refunded: number; refund_error: string | null; owed: number }>(`/admin/hq/orders/${id}/remove-item`, body),
  addOrderItem: (id: string, body: { variant_id: string; quantity: number; unit_price: number | null; request_id: string }) =>
    post<{ order: OrderDetail; replay: boolean; owed: number }>(`/admin/hq/orders/${id}/add-item`, body),
  refundOrder: (id: string, body: { amount: number; reason: string | null; notify: boolean }) => post<{ order: OrderDetail }>(`/admin/hq/orders/${id}/refund`, body),
  resendOrderEmail: (id: string, body: { type: OrderEmailType; id: string; request_id: string }) =>
    post<{ order: OrderDetail; resend: EmailResend; replay: boolean }>(`/admin/hq/orders/${id}/resend-email`, body),
  deliveryCharge: (id: string, body: { amount: number; request_id: string; note: string | null; refund: boolean; notify: boolean }) =>
    post<{ order: OrderDetail; replay: boolean; refunded: number; refund_error: string | null; owed: number }>(`/admin/hq/orders/${id}/delivery-charge`, body),
  freeDelivery: () => sdk.client.fetch<FreeDeliverySettings>("/admin/hq/settings/free-delivery"),
  saveFreeDelivery: (body: { threshold: number | null; methods: FreeDeliveryMethod[] }) => post<FreeDeliverySettings>("/admin/hq/settings/free-delivery", body),
  receiveReturn: (id: string, body: ReturnBody) =>
    post<{ order: OrderDetail; result: { return_id: string; replay: boolean; order_ref: string; refund_amount: number } }>(`/admin/hq/orders/${id}/returns`, body),
  exchangeSent: (returnId: string, body: { way: ReturnWay; tracking_number: string | null; notify: boolean }) =>
    post<{ order: OrderDetail; result: { id: string; order_id: string; replay: boolean } }>(`/admin/hq/returns/${returnId}/sent`, body),
  notes: (key: string) => sdk.client.fetch<{ notes: OrderNote[] }>(`/admin/hq/notes?key=${encodeURIComponent(key)}`),
  addNote: (key: string, body: string) => post<{ note: OrderNote }>("/admin/hq/notes", { key, body }),
  deleteNote: (id: string) => post<{ id: string }>(`/admin/hq/notes/${id}/delete`),
  discounts: () => sdk.client.fetch<{ discounts: Discount[] }>("/admin/hq/discounts"),
  createDiscount: (body: NewDiscount) => post<{ code: string; promotion_id: string; replay: boolean }>("/admin/hq/discounts", body),
  setDiscountActive: (id: string, active: boolean) => post<{ id: string; status: string }>(`/admin/hq/discounts/${id}/status`, { active }),
  pulse: () => sdk.client.fetch<Pulse>("/admin/hq/analytics/pulse"),
  stockOutlook: () => sdk.client.fetch<StockOutlook>("/admin/hq/stock/outlook"),
  sales: (params: { from?: string; to?: string; channels: ChannelKey[] }) => {
    const query = new URLSearchParams({ channels: params.channels.join(","), ...(params.from ? { from: params.from } : {}), ...(params.to ? { to: params.to } : {}) })
    return sdk.client.fetch<SalesReport>(`/admin/hq/analytics/sales?${query}`)
  },
  insights: () => sdk.client.fetch<InsightsReport>("/admin/hq/analytics/insights"),
  importShopify: (body: { orders: unknown[]; products: unknown[]; dry_run: boolean }) =>
    post<{ dry_run: boolean; summary: ImportSummary }>("/admin/hq/imports/shopify", body),
  customOrders: () => sdk.client.fetch<{ configured: boolean; sync: CustomSync | null; orders: CustomOrderListItem[] }>("/admin/hq/custom/orders"),
  customOrder: (number: string) => sdk.client.fetch<CustomOrderPage | CustomTestOnlyPage>(`/admin/hq/custom/orders/${encodeURIComponent(number)}`),
  customNewContext: () => sdk.client.fetch<CustomNewContext>("/admin/hq/custom/new"),
  customPreview: (body: { items: Array<{ en: string; yuan: number | null; eur: number | null }>; shipping: string; discount: number; prepay_amount: number | null; prepay_method: string }) => post<CustomPreview>("/admin/hq/custom/preview", body),
  createCustomOrder: (request_id: string, order: NewCustomOrderBody) => post<CustomCreated>("/admin/hq/custom/orders", { request_id, order }),
  customDetails: (number: string, body: CustomDetailsBody) => post<CustomWritten>(`/admin/hq/custom/orders/${encodeURIComponent(number)}/details`, body),
  customPrices: (number: string, body: { items: Array<{ oos: boolean; eur: number | null }>; total: number | null }) =>
    post<CustomWritten>(`/admin/hq/custom/orders/${encodeURIComponent(number)}/prices`, body),
  // A backend from before 21 Oct has no /sold route: the per-item prices then go to the sheet only (/prices).
  customSold: async (number: string, body: { items: Array<{ oos: boolean; eur: number | null }> }) => {
    try {
      return await post<{ remembered: boolean; sheet: CustomWritten | null; sheet_error: string | null }>(`/admin/hq/custom/orders/${encodeURIComponent(number)}/sold`, body)
    } catch (error) {
      if ((error as { status?: number })?.status !== 404) throw error
      const sheet = await post<CustomWritten>(`/admin/hq/custom/orders/${encodeURIComponent(number)}/prices`, { items: body.items, total: null })
      return { remembered: false, sheet, sheet_error: null }
    }
  },
  customAddItems: (number: string, items: CustomIntakeItem[]) => post<CustomWritten>(`/admin/hq/custom/orders/${encodeURIComponent(number)}/add-items`, { items }),
  customItems: (number: string, items: CustomIntakeItem[], photos?: { image: string | null }) => post<CustomWritten>(`/admin/hq/custom/orders/${encodeURIComponent(number)}/items`, { items, ...(photos ? { photos } : {}) }),
  customLarryPhotos: (number: string) => post<CustomWritten & { photos_added?: number }>(`/admin/hq/custom/orders/${encodeURIComponent(number)}/larry-photos`, {}),
  customPaid: (number: string) => post<CustomWritten>(`/admin/hq/custom/orders/${encodeURIComponent(number)}/paid`, {}),
  customToLarry: (number: string) => post<CustomWritten>(`/admin/hq/custom/orders/${encodeURIComponent(number)}/larry`, {}),
  paymentLinks: (order?: string) => sdk.client.fetch<{ configured: boolean; links: PaymentLinkView[] }>(`/admin/hq/payment-links${order ? `?order=${encodeURIComponent(order)}` : ""}`),
  createPaymentLink: (body: NewPaymentLink) => post<{ link: PaymentLinkView; replay: boolean }>("/admin/hq/payment-links", body),
  cancelPaymentLink: (id: string) => post<{ id: string; status: string }>(`/admin/hq/payment-links/${id}/cancel`),
  refundPaymentLink: (id: string, amountCents: number, requestId: string) => post<{ id: string; amount_cents: number }>(`/admin/hq/payment-links/${id}/refund`, { amount_cents: amountCents, request_id: requestId }),
  refreshPaymentLinks: () => post<{ checked: number; paid: string[]; errors: number }>("/admin/hq/payment-links/refresh"),
  shipCustom: (number: string, body: { tracking_number: string; notify: boolean; email: string | null }) =>
    post<{ order_number: string; voucher: string; url: string; replay: boolean; emailed: { sent: boolean; to: string | null; mode: string | null; reason: string | null } }>(
      `/admin/hq/custom/orders/${encodeURIComponent(number)}/ship`,
      body
    ),
  setCustomStatus: (number: string, status: CustomStatus) =>
    post<{ order_number: string; from: string; to: CustomStatus; target: BridgeTarget; tab: string; row: number; took_ms: number; synced: boolean }>(
      `/admin/hq/custom/orders/${encodeURIComponent(number)}/status`,
      { status }
    ),
  corrections: () => sdk.client.fetch<{ corrections: CorrectionRow[] }>("/admin/hq/custom/corrections"),
  decideCorrection: (number: string, state: "applied" | "dismissed" | "pending") => post<{ order_number: string; state: string }>(`/admin/hq/custom/corrections/${encodeURIComponent(number)}`, { state }),
  correctOrder: (number: string, body: { sale_total_eur: number | null; cost_total_eur: number | null; note?: string | null }) =>
    post<{ order_number: string; state: string }>(`/admin/hq/custom/corrections/${encodeURIComponent(number)}`, body),
  importPlatform: (data: unknown) => post<{ summary: PlatformImportSummary }>("/admin/hq/imports/platform", data),
  replies: () => sdk.client.fetch<{ replies: ReplyTemplate[] }>("/admin/hq/replies"),
  saveReply: (id: string | null, body: { title: string; body: string }) => post<{ id: string }>(id ? `/admin/hq/replies/${id}` : "/admin/hq/replies", body),
  deleteReply: (id: string) => post<{ id: string }>(`/admin/hq/replies/${id}/delete`),
  orderReplies: (ids: string[]) => post<{ ordered: number }>("/admin/hq/reply-order", { ids }),
  suppliers: () => sdk.client.fetch<{ suppliers: SupplierLink[] }>("/admin/hq/suppliers"),
  saveSupplier: (id: string | null, body: SupplierBody) => post<{ id: string }>(id ? `/admin/hq/suppliers/${id}` : "/admin/hq/suppliers", body),
  deleteSupplier: (id: string) => post<{ id: string }>(`/admin/hq/suppliers/${id}/delete`),
  customSync: () => post<CustomSync & { changed: boolean }>("/admin/hq/custom/sync"),
  costs: () => sdk.client.fetch<CostsReport>("/admin/hq/costs"),
  saveCost: (id: string | null, rule: CostRuleInput) => post<{ rule: CostRule }>(id ? `/admin/hq/costs/${id}` : "/admin/hq/costs", rule),
  deleteCost: (id: string) => sdk.client.fetch<{ id: string; deleted: boolean }>(`/admin/hq/costs/${id}`, { method: "DELETE" }),
  larryBulk: () => sdk.client.fetch<BulkOrder>("/admin/hq/costs/larry-bulk"),
  forecast: () => sdk.client.fetch<ForecastReport>("/admin/hq/forecast"),
  deliveryCosts: (months = 12) => sdk.client.fetch<DeliveryReport>(`/admin/hq/costs/delivery?months=${months}`),
  productProfit: () => sdk.client.fetch<ProductProfit>("/admin/hq/products/profit"),
  stockLots: () => sdk.client.fetch<StockLotsReport>("/admin/hq/stock/lots"),
  planner: () => sdk.client.fetch<PlannerReport>("/admin/hq/planner"),
  alerts: () => sdk.client.fetch<{ alerts: HqAlert[]; last_digest: { day: string; sent_at: string } | null }>("/admin/hq/alerts"),
  /** The urgent "card charged but no order" alert: make the order from the same cart (never a second charge). */
  completePaidCart: (cartId: string) => post<{ order: { order_id: string; display_id: number | null } }>(`/admin/hq/carts/${encodeURIComponent(cartId)}/complete`),
  refreshAlerts: (sendDigest = false) => post<{ open: number; created: number; resolved: number; notified: number; digest: "sent" | "failed" | "not_due" }>("/admin/hq/alerts/refresh", { send_digest: sendDigest }),
  productSetup: () => sdk.client.fetch<ProductSetup>("/admin/hq/products/setup"),
  createdProducts: () => sdk.client.fetch<{ products: CreatedProduct[] }>("/admin/hq/products/created"),
  createProduct: (body: NewProductBody) => post<{ product_id: string; handle: string; replay: boolean }>("/admin/hq/products/created", body),
  publishProduct: (id: string) => post<{ product_id: string; status: string }>(`/admin/hq/products/created/${id}/publish`),
  productItems: () => sdk.client.fetch<{ products: ProductListItem[] }>("/admin/hq/products/items"),
  catalogHealth: () => sdk.client.fetch<{ products: CatalogHealthProduct[]; preview_min_width: number; checked_at: string }>("/admin/hq/products/health"),
  productItem: (id: string) => sdk.client.fetch<{ product: EditableProduct }>(`/admin/hq/products/items/${id}`),
  extras: () => sdk.client.fetch<{ groups: ExtraGroup[] }>("/admin/hq/extras"),
  shopOrder: () => sdk.client.fetch<{ ranked: boolean; products: ShopOrderProduct[] }>("/admin/hq/shop-order"),
  saveShopOrder: (ids: string[]) => post<{ changed: number; products: number }>("/admin/hq/shop-order", { ids }),
  createExtraGroup: (body: ExtraGroupBody & { request_id: string }) => post<{ id: string; replay: boolean }>("/admin/hq/extras", body),
  saveExtraGroup: (id: string, body: ExtraGroupBody) => post<{ id: string; changes: string[] }>(`/admin/hq/extras/${id}`, body),
  deleteExtraGroup: (id: string) => post<{ id: string; deleted: boolean }>(`/admin/hq/extras/${id}/delete`, {}),
  trackExtraStock: (id: string, counts: Record<string, number>) => post<{ group_id: string; choices: number; reserved: number }>(`/admin/hq/extras/${id}/track-stock`, { counts }),
  editProduct: (id: string, body: EditProductBody) => post<{ product_id: string; changes: string[]; replay: boolean }>(`/admin/hq/products/items/${id}`, body),
  deleteDraft: (id: string) => post<{ product_id: string; title: string }>(`/admin/hq/products/items/${id}/delete`),
  unpublishProduct: (id: string) => post<{ product_id: string; status: string }>(`/admin/hq/products/items/${id}/unpublish`),
  addProductPhotos: (id: string, images: string[]) => post<{ product_id: string; photos: number }>(`/admin/hq/products/created/${id}/photos`, { images }),
  stockChanges: (limit = 30) => sdk.client.fetch<{ changes: StockChangeEntry[] }>("/admin/hq/stock/movements", { query: { limit } }),
  changeStock: (body: StockChangeBody) =>
    post<{ replay: boolean; changes: Array<{ variant_id: string; option: string; delta: number; stocked_before: number; stocked_after: number; reserved: number }> }>("/admin/hq/stock/changes", body),
  productMerges: () => sdk.client.fetch<{ merges: ProductMerge[] }>("/admin/hq/products/merges"),
  mergeProducts: (body: { from_key: string; to_key: string; to_title: string }) => post<{ merge: ProductMerge }>("/admin/hq/products/merges", body),
  unmergeProduct: (id: string) => sdk.client.fetch<{ id: string }>(`/admin/hq/products/merges/${id}`, { method: "DELETE" }),
  money: (months = 12) => sdk.client.fetch<MoneyReport>(`/admin/hq/money?months=${months}`),
  tiedUp: () => sdk.client.fetch<TiedUpReport>("/admin/hq/money/tied-up"),
  customBank: () => sdk.client.fetch<CustomBank>("/admin/hq/money/custom-bank"),
  larry: () => sdk.client.fetch<LarryReport>("/admin/hq/money/larry"),
  stockInvestments: () => sdk.client.fetch<StockInvestments>("/admin/hq/money/stock-investments"),
  moneyLedger: (params: { month?: string; unclassified?: boolean }) => {
    const query = new URLSearchParams({ ...(params.month ? { month: params.month } : {}), ...(params.unclassified ? { only: "unclassified" } : {}) })
    return sdk.client.fetch<{ month: string | null; entries: LedgerLine[]; rules: Array<{ id: string; match: string; category: ExpenseCategory }> }>(`/admin/hq/money/ledger?${query}`)
  },
  classify: (body: { entry_key: string; category: ExpenseCategory | null; rule_match?: string | null }) => post<{ entry_key: string }>("/admin/hq/money/classify", body),
  addFixedCost: (body: Omit<FixedCost, "id">) => post<{ fixed_cost: FixedCost }>("/admin/hq/money/fixed", body),
  removeFixedCost: (id: string) => sdk.client.fetch<{ id: string }>(`/admin/hq/money/fixed/${id}`, { method: "DELETE" }),
  purchaseOrders: () => sdk.client.fetch<{ orders: PurchaseOrder[] }>("/admin/hq/purchase-orders"),
  purchaseOrder: (id: string) => sdk.client.fetch<{ order: PurchaseOrder }>(`/admin/hq/purchase-orders/${id}`),
  createPurchaseOrder: (body: NewPurchaseOrderBody) => post<{ order: PurchaseOrder; replay: boolean }>("/admin/hq/purchase-orders", body),
  receivePurchaseOrder: (id: string, body: ReceivePurchaseBody) => post<{ order: PurchaseOrder; replay: boolean; received: number }>(`/admin/hq/purchase-orders/${id}/receive`, body),
  closePurchaseOrder: (id: string, closed: boolean) => post<{ order: PurchaseOrder }>(`/admin/hq/purchase-orders/${id}/close`, { closed }),
  editPurchaseLines: (id: string, lines: Array<{ id: string; not_coming?: number; unit_cost_eur?: number | null }>) =>
    post<{ order: PurchaseOrder }>(`/admin/hq/purchase-orders/${id}/lines`, { lines }),
  deletePurchaseOrder: (id: string) => post<{ order_id: string; deleted: boolean }>(`/admin/hq/purchase-orders/${id}/delete`),
  parcels: () => sdk.client.fetch<ParcelsReport>("/admin/hq/parcels"),
  refreshParcels: (body: { photos?: boolean; tracking?: boolean; key?: string | null }) =>
    post<{
      parcels: { changed: boolean; batches: number }
      photos: { state: string; uploaded: number; photos: number; error?: string } | null
      tracking: { state: string; checked: number; found: number; errors: number; limited: boolean } | null
    }>("/admin/hq/parcels/refresh", body),
  setParcelsReceived: (trackings: string[], received: boolean) => post<{ trackings: string[]; received: boolean; changed: number }>("/admin/hq/parcels/received", { trackings, received }),
  reportParcelProblem: (body: { key: string; orders: string[]; stock: boolean; note: string | null }) => post<{ id: string; created: boolean }>("/admin/hq/parcels/problems", body),
  sortParcelProblem: (id: string, body: { sorted: boolean; note?: string | null }) => post<{ id: string; status: "open" | "sorted" }>(`/admin/hq/parcels/problems/${encodeURIComponent(id)}`, body),
  setCustomOrderState: (number: string, body: CustomOrderStateBody) => post<{ order_number: string } & ContactedResult>(`/admin/hq/custom/orders/${encodeURIComponent(number)}/state`, body),
  purchaseOrderFromBulk: (requestId: string) => post<{ order: PurchaseOrder; replay: boolean }>("/admin/hq/purchase-orders/from-bulk", { request_id: requestId }),
  imports: () => sdk.client.fetch<{ imports: Array<{ id: string; source: string; created_at: string; summary: ImportSummary }> }>("/admin/hq/imports"),
}

/**
 * What went wrong, in words, in the chosen language: the server's own message (translated by its
 * English template, see i18n ts()), else its status or the error's kind.
 */
export const errorMessage = (error: unknown) => {
  const e = error as { message?: string; status?: number; name?: string } | null
  if (e?.message) return ts(e.message)
  if (e?.status) return t("Something went wrong (the server answered {status}).", { status: String(e.status) })
  if (e?.name && e.name !== "Error") return t("Something went wrong ({name}).", { name: e.name })
  return t("Something went wrong.")
}
